import {Trans} from '@lingui/macro';
import _ from 'lodash';
import {ellipsis} from 'polished';
import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {HandlerOf} from '../../../../../../../libs/shared/helpers/src/react/reactHelpers';
import {DropdownBox} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/dropdownBox';
import {TextBox} from '../../../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {Dropdown} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownItem} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItem';
import {DropdownItemCheckmark} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemCheckmark';
import {DropdownItemContent} from '../../../../../../../libs/shared/ui/src/components/dropdowns/dropdownItemContent';
import {PopoverCoordinator} from '../../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {TooltipCoordinator} from '../../../../../../../libs/shared/ui/src/components/tooltips/tooltipCoordinator';
import {fontSizes, fontWeights} from '../../../../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../../../../libs/shared/ui/src/theme/userThemeProvider';
import {DropdownItemIcon} from '../../../../../../components/common/dropdowns/dropdownItemIcon';
import {DropdownVirtualList} from '../../../../../../components/common/dropdowns/dropdownVirtualList';
import {TypedRowValue} from '../../../../../../components/common/dropdowns/source/dropdownSource';
import {Link} from '../../../../../../components/common/links/link';
import {TooltipExplicitCondition} from '../../../../../../components/common/tooltips/tooltipExplicitCondition';
import {renderLocalApplicationServerAuthenticationConfigStrategy} from '../../../../../../helpers/localApplicationHelpers';
import {LocalApplicationModel} from '../../../../../../models/api/localApplicationModel';
import {LocalApplicationServerModel} from '../../../../../../models/api/localApplicationServerModel';
import {buildSettingsDevelopersPath} from '../../../../../../models/url/settingsUrlHelpers';
import {
  SettingsDevelopersPagesEnum,
  SettingsRootPagesEnum,
} from '../../../../../../models/url/settingsUrlModel';
import {SettingsDevelopersConnectServerDialog} from '../../../../dialog/settingsDevelopersConnectServerDialog';

/*
 * Constants.
 */

const dropdownWidth = 171;
const dropdownMaxHeight = 260;
const dropdownRow = 30;
const dropdownServerRow = 56;

enum ServerDropdownTypesEnum {
  HEADING = 'heading',
  CREATE = 'create',
  SERVER = 'server',
}
type ServerHeadingDropdownTypedRowValue = TypedRowValue<ServerDropdownTypesEnum.HEADING, React.ReactNode>;
type ServerConnectDropdownTypedRowValue = TypedRowValue<ServerDropdownTypesEnum.CREATE, null>;
type ServerValueDropdownTypedRowValue = TypedRowValue<
  ServerDropdownTypesEnum.SERVER,
  LocalApplicationServerModel
>;
type ServerDropdownItemsType =
  | ServerHeadingDropdownTypedRowValue
  | ServerConnectDropdownTypedRowValue
  | ServerValueDropdownTypedRowValue;

/*
 * Props.
 */

interface SettingsDevelopersChannelWebhookUrlInputProps {
  application: LocalApplicationModel;
  servers: ReadonlyArray<LocalApplicationServerModel>;
  webhookUrlOrigin: string;
  webhookUrlPath: string;
  onWebhookUrlOriginChange: HandlerOf<string>;
  onWebhookUrlPathChange: HandlerOf<string>;
  onWebhookUrlValidationChange: HandlerOf<boolean>;
}

/*
 * Style.
 */

const StyledWebhookUrlWrapperDiv = styled.div`
  display: flex;
  flex-flow: row;
  gap: 4px;
`;

const StyledWebhookOriginDropdownBox = styled(DropdownBox)`
  width: 171px;
`;

const StyledWebhookPathTextBox = styled(TextBox)`
  flex: 1;
`;

const StyledMessageWrapperDiv = styled.div`
  white-space: nowrap;
`;

const StyledPlaceholderDiv = styled.div`
  color: ${(p) => p.theme.greys.shade60};
`;

const StyledDropdownItemTitleDiv = styled.div`
  display: flex;
  flex-flow: column;
  justify-content: center;
  color: ${(p) => p.theme.greys.shade60};
  font-size: ${fontSizes.verySmall};
  padding: 0 12px;
  height: 100%;
  font-weight: ${fontWeights.semibold};
`;

const StyledDropdownItemServerTitleDiv = styled.div`
  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};
  ${ellipsis()};
`;

const StyledDropdownItemServerDescriptionDiv = styled.div`
  color: ${(p) => p.theme.greys.shade70};
  font-size: ${fontSizes.verySmall};
`;

const StyledServerDropdownItem = styled(DropdownItem)`
  height: 100%;
`;

const StyledTooltipCoordinator = styled(TooltipCoordinator)`
  display: flex;
  flex-flow: column;
  justify-content: center;
  height: 100%;
`;

/*
 * Component.
 */

export const SettingsDevelopersChannelWebhookUrlInput: FC<SettingsDevelopersChannelWebhookUrlInputProps> = (
  props,
) => {
  const {
    application,
    servers,
    webhookUrlOrigin,
    webhookUrlPath,
    onWebhookUrlOriginChange,
    onWebhookUrlPathChange,
    onWebhookUrlValidationChange,
  } = props;
  const theme = useUserTheme();
  const customAnchorRef = useRef<HTMLDivElement>(null);
  const [isShowingCreateDialog, setIsShowingCreateDialog] = useState(false);

  const dropdownItems = useMemo(() => buildDropdownItems(servers), [servers]);
  const errorMessage = useMemo(
    () => computeErrorMessage(servers, webhookUrlOrigin, webhookUrlPath),
    [servers, webhookUrlOrigin, webhookUrlPath],
  );

  useEffect(() => {
    if (errorMessage) {
      onWebhookUrlValidationChange(false);
      return;
    }
    onWebhookUrlValidationChange(webhookUrlOrigin !== '' && webhookUrlPath !== '');
  }, [onWebhookUrlValidationChange, errorMessage, webhookUrlOrigin, webhookUrlPath]);

  return (
    <>
      <StyledWebhookUrlWrapperDiv>
        <PopoverCoordinator
          renderButton={(hasHover) => (
            <StyledWebhookOriginDropdownBox
              ref={customAnchorRef}
              hasHover={hasHover}
              noMargin
              boxStyles={{
                width: '171px',
                height: '30px',
                borderTopRightRadius: 0,
                borderBottomRightRadius: 0,
                marginTop: 0,
              }}
              value={renderOriginPlaceholderOrValue(webhookUrlOrigin)}
              errorMessage={errorMessage}
              hasError={Boolean(errorMessage)}
              message={
                <StyledMessageWrapperDiv>
                  <Trans id="developers-application-feature-channel-message-1">
                    Select an origin from the
                  </Trans>{' '}
                  <Link
                    to={buildSettingsDevelopersPath({
                      rootPage: SettingsRootPagesEnum.DEVELOPERS,
                      subPage: SettingsDevelopersPagesEnum.APPLICATION_SERVERS,
                      applicationId: application.id,
                    })}
                  >
                    <Trans id="developers-application-feature-channel-message-servers">Servers</Trans>
                  </Link>{' '}
                  <Trans id="developers-application-feature-channel-message-2">
                    page and fill in the path to complete the webhook URL.
                  </Trans>
                </StyledMessageWrapperDiv>
              }
            />
          )}
          renderModal={(popoverProps) => (
            <Dropdown anchor={customAnchorRef} onRequestClose={popoverProps.onRequestClose}>
              <DropdownHeader
                title={
                  <Trans id="developers-application-feature-channel-server-dropdown-title">Origin</Trans>
                }
              />
              <DropdownVirtualList
                width={dropdownWidth}
                maxHeight={dropdownMaxHeight}
                rowCount={dropdownItems.length}
                getRowHeight={(index) => {
                  if (dropdownItems[index].type === ServerDropdownTypesEnum.SERVER) {
                    return dropdownServerRow;
                  }
                  return dropdownRow;
                }}
                renderRow={(index) => {
                  const item = dropdownItems[index];
                  if (item.type === ServerDropdownTypesEnum.HEADING) {
                    return <StyledDropdownItemTitleDiv>{item.value}</StyledDropdownItemTitleDiv>;
                  }
                  if (item.type === ServerDropdownTypesEnum.CREATE) {
                    return (
                      <DropdownItem
                        onSelect={() => {
                          setIsShowingCreateDialog(true);
                          popoverProps.onRequestClose();
                        }}
                      >
                        <DropdownItemIcon
                          iconName="plusCircleFilledSmall"
                          label={null}
                          color={theme.palette.blue.shade40}
                          iconSize={16}
                        />
                        <DropdownItemContent>
                          <Trans id="developers-application-feature-channel-server-dropdown-new-server">
                            New server
                          </Trans>
                        </DropdownItemContent>
                      </DropdownItem>
                    );
                  }
                  const serverIsHttps = item.value.origin.startsWith('https://');
                  return (
                    <StyledServerDropdownItem
                      onSelect={() => {
                        onWebhookUrlOriginChange(item.value.origin);
                        popoverProps.onRequestClose();
                      }}
                      // We can only support https origins for webhook.
                      isDisabled={!serverIsHttps}
                    >
                      <DropdownItemCheckmark isChecked={webhookUrlOrigin === item.value.origin} />
                      <DropdownItemContent>
                        <StyledTooltipCoordinator
                          render={() => (
                            <Trans id="developers-application-feature-channel-server-dropdown-https-required">
                              Only HTTPS origins are allowed.
                            </Trans>
                          )}
                        >
                          <TooltipExplicitCondition isEnabled={!serverIsHttps} />
                          <StyledDropdownItemServerTitleDiv>
                            {item.value.origin}
                          </StyledDropdownItemServerTitleDiv>
                          <StyledDropdownItemServerDescriptionDiv>
                            {renderLocalApplicationServerAuthenticationConfigStrategy(
                              item.value.authenticationConfig.type,
                            )}
                          </StyledDropdownItemServerDescriptionDiv>
                        </StyledTooltipCoordinator>
                      </DropdownItemContent>
                    </StyledServerDropdownItem>
                  );
                }}
              />
            </Dropdown>
          )}
        />
        <StyledWebhookPathTextBox
          boxStyles={{borderTopLeftRadius: 0, borderBottomLeftRadius: 0}}
          value={webhookUrlPath}
          onChange={(value) => {
            if (!value.startsWith('/') && value !== '') {
              onWebhookUrlPathChange(`/${value}`);
              return;
            }
            onWebhookUrlPathChange(value);
          }}
          noMargin
          placeholder="/acme_channel"
          hasError={Boolean(errorMessage)}
        />
      </StyledWebhookUrlWrapperDiv>
      {isShowingCreateDialog && (
        <SettingsDevelopersConnectServerDialog
          localApplicationId={application.id}
          servers={servers}
          onRequestClose={(val) => {
            if (val) {
              onWebhookUrlOriginChange(val);
            }
            setIsShowingCreateDialog(false);
          }}
        />
      )}
    </>
  );
};

/*
 * Helpers.
 */

function computeErrorMessage(
  servers: ReadonlyArray<LocalApplicationServerModel>,
  origin: string,
  path: string,
) {
  if (origin === '' && path === '') {
    return undefined;
  }
  const foundOrigin = servers.find((server) => server.origin === origin);
  if (!foundOrigin && origin !== '') {
    return (
      <StyledMessageWrapperDiv>
        <Trans id="developers-application-feature-channel-webhook-error-not-found">
          This server origin no longer exists. Select another origin or create a new one.
        </Trans>
      </StyledMessageWrapperDiv>
    );
  }
  if (origin === '') {
    return (
      <StyledMessageWrapperDiv>
        <Trans id="developers-application-feature-channel-webhook-error-origin-required">
          The origin is a required selection.
        </Trans>
      </StyledMessageWrapperDiv>
    );
  }
  if (path === '') {
    return (
      <StyledMessageWrapperDiv>
        <Trans id="developers-application-feature-channel-webhook-error-path-required">
          The path is a required field.
        </Trans>
      </StyledMessageWrapperDiv>
    );
  }
  return undefined;
}

function renderOriginPlaceholderOrValue(selectedWebhookOrigin: string) {
  if (selectedWebhookOrigin === '') {
    return (
      <StyledPlaceholderDiv>
        <Trans id="developers-application-feature-channel-webhook-placeholder">Select origin</Trans>
      </StyledPlaceholderDiv>
    );
  }
  return <>{selectedWebhookOrigin}</>;
}

function buildDropdownItems(
  servers: ReadonlyArray<LocalApplicationServerModel>,
): ReadonlyArray<ServerDropdownItemsType> {
  const values: ReadonlyArray<ServerDropdownItemsType> = [
    // If we are searching, we can remove the base items.
    {
      type: ServerDropdownTypesEnum.HEADING,
      value: (
        <Trans id="developers-application-feature-channel-server-dropdown-connect-heading">Connect</Trans>
      ),
    },
    {
      type: ServerDropdownTypesEnum.CREATE,
      value: null,
    },
    {
      type: ServerDropdownTypesEnum.HEADING,
      value: (
        <Trans id="developers-application-feature-channel-server-dropdown-origins-heading">Origins</Trans>
      ),
    },
    ..._([...servers])
      .map<ServerValueDropdownTypedRowValue>((server) => ({
        type: ServerDropdownTypesEnum.SERVER,
        value: server,
      }))
      .value(),
  ];

  // If we do not have servers, we will only have 3 items and we can remove the last item.
  if (values.length === 3) {
    return values.slice(0, 2);
  }
  return values;
}
